import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, from, lastValueFrom, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, take, tap } from 'rxjs/operators';
import { Loader } from '@googlemaps/js-api-loader';
import { Address, AddressModelKeys } from '@ag-common-lib/public-api';
import { GOOGLE_MAPS_API_KEY } from '@ag-common-lib/lib/models/utils/google-maps-injection-token';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AddressValidationResponse } from '@ag-common-lib/lib';

@Injectable({ providedIn: 'root' })
export class GoogleMapsService {
  private apiKey = this.googleMapsApiKey;
  private ADDRESS_VALIDATION_API_URL = 'https://addressvalidation.googleapis.com/v1:validateAddress';

  private readonly loader = new Loader({
    apiKey: this.apiKey,
    version: 'weekly',
    libraries: ['places', 'geometry', 'routes'],
  });

  private _sessionToken;
  private _resetSessionTokenTimeoutId;

  userPosition: { lat: number; lng: number };

  userPosition$ = new BehaviorSubject(null);

  readonly placesLib$ = from(this.loader.importLibrary('places')).pipe(shareReplay(1));
  readonly mapsLib$ = from(this.loader.importLibrary('maps')).pipe(shareReplay(1));
  readonly markerLib$ = from(this.loader.importLibrary('marker')).pipe(shareReplay(1));
  readonly geocodingLib$ = from(this.loader.importLibrary('geocoding')).pipe(shareReplay(1));
  // markerLib$ = from(this.loader.importLibrary('v')).pipe(shareReplay(1));

  constructor(
    private http: HttpClient,
    @Inject(GOOGLE_MAPS_API_KEY) private googleMapsApiKey: string,
  ) {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        const userPosition = { lat: position.coords.latitude, lng: position.coords.longitude };
        this.userPosition = userPosition;
        this.userPosition$.next(userPosition);
      });
    }
  }

  initMap = async (): Promise<google.maps.MapsLibrary> => {
    return firstValueFrom(this.mapsLib$);
  };

  initMarkers = async (): Promise<google.maps.MarkerLibrary> => {
    return firstValueFrom(this.markerLib$);
  };

  getDetails = async (placeId: string) => {
    this._sessionToken = null;

    const predictions = await lastValueFrom(
      this.placesLib$.pipe(
        mergeMap((placesLib): Promise<any> => {
          const placesService = new placesLib.PlacesService(document.createElement('div'));
          const request: google.maps.places.PlaceDetailsRequest = {
            placeId: placeId,
            fields: ['id', 'name', 'address_component', 'geometry.location'],
          };

          return new Promise(res => {
            placesService.getDetails(request, (place, status) => {
              if (status !== google.maps.places.PlacesServiceStatus.OK) {
                res(null);
                return;
              }
              // set placeId on address details returned from Google to fix google map error on save address
              res({
                ...this.getAddress(place),
                [AddressModelKeys.placeId]: placeId,
              });
            });
          });
        }),

        catchError(() => {
          return of(null);
        }),
        take(1),
      ),
    );

    return predictions;
  };

  geocodeCoordinates = async location => {
    const address = await lastValueFrom(
      this.geocodingLib$.pipe(
        mergeMap((geocodingLib): Promise<any> => {
          return new geocodingLib.Geocoder().geocode({ location });
        }),
        map(response => {
          const results = response?.results;

          if (!results?.[0]) {
            return null;
          }

          const address = this.getAddress(results?.[0]);

          return address;
        }),
        catchError(() => {
          return of(null);
        }),
        take(1),
      ),
    );

    return address;
  };

  validateAddress(address: Address): Promise<AddressValidationResponse> {
    this._sessionToken = null;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    const addressLines = this.getAddressLines(address);
    const body = {
      address: {
        regionCode: 'US',
        locality: address[AddressModelKeys.city],
        administrativeArea: address[AddressModelKeys.state],
        postalCode: address[AddressModelKeys.zip],
        addressLines: addressLines,
      },
      enableUspsCass: true,
      previousResponseId: address?.[AddressModelKeys.validationResponse]?.responseId,
    };

    return lastValueFrom(
      this.http
        .post<AddressValidationResponse>(`${this.ADDRESS_VALIDATION_API_URL}?key=${this.apiKey}`, body, { headers })
        .pipe(take(1), tap(console.log)),
    );
  }

  getAddressLines(address: Address): string[] {
    const addressLines: string[] = [];

    if (address?.[AddressModelKeys.address1]) {
      addressLines.push(address?.[AddressModelKeys.address1]);
    }
    if (address?.[AddressModelKeys.address2]) {
      addressLines.push(`#(${address?.[AddressModelKeys.address2]})`);
    }

    // // Optionally include city, state, and ZIP on separate lines or combined
    // const cityStateZip = [
    //   address?.[AddressModelKeys.city],
    //   address?.[AddressModelKeys.state],
    //   address?.[AddressModelKeys.zip],
    // ]
    //   .filter(Boolean)
    //   .join(', ');

    // if (cityStateZip) {
    //   addressLines.push(cityStateZip);
    // }

    return addressLines;
  }

  private getAddress = (place: google.maps.places.PlaceResult) => {
    const location = place.geometry.location;
    const items = place?.address_components?.reduce(
      (acc, component) => {
        const types = new Set(component?.types);
        if (types.has('street_number')) {
          acc.streetNumber = component.long_name;
        }
        if (types.has('route')) {
          acc.route = component.long_name;
        }
        if (types.has('subpremise')) {
          acc.subpremise = component.long_name;
        }
        if (types.has('locality')) {
          acc.city = component.long_name;
        }
        if (types.has('neighborhood')) {
          acc.neighborhood = component.long_name;
        }
        if (types.has('administrative_area_level_1')) {
          acc.state = component.short_name; // State (use short_name for abbreviations like "CA" for California)
        }
        if (types.has('administrative_area_level_2')) {
          acc.county = component.short_name; // State (use short_name for abbreviations like "CA" for California)
        }
        if (types.has('country')) {
          acc.country = component.long_name; // Country
        }
        if (types.has('postal_code')) {
          acc.zipCode = component.long_name; // Postal code
        }

        return acc;
      },
      {
        streetNumber: null,
        route: null,
        subpremise: null,
        city: null,
        neighborhood: null,
        state: null,
        zipCode: null,
        country: null,
        county: null,
      },
    );

    const address: Address = {
      [AddressModelKeys.geoPoint]: {
        [AddressModelKeys.lat]: location?.lat(),
        [AddressModelKeys.lng]: location?.lng(),
      },
    };

    address[AddressModelKeys.address1] = [items?.streetNumber, items?.route].filter(Boolean).join(' ');
    address[AddressModelKeys.address2] = items?.subpremise;
    address[AddressModelKeys.country] = items?.country;
    address[AddressModelKeys.county] = items?.county;
    address[AddressModelKeys.state] = items?.state;
    address[AddressModelKeys.zip] = items?.zipCode;
    address[AddressModelKeys.city] = items?.city;

    // const [mainCityItem, ...restCityItems] = [items?.neighborhood, items?.city].filter(Boolean);
    // const restCityName = restCityItems?.filter(Boolean).join(', ');

    // let city = mainCityItem;

    // if (restCityName) {
    //   city = mainCityItem ? `${mainCityItem} (${restCityName})` : mainCityItem;
    // }

    // address[AddressModelKeys.city] = city;

    return address;
  };

  getGoogleSuggestions = async (
    searchValue: string,
  ): Promise<google.maps.places.AutocompleteSuggestion[]> => {
    const sessionToken = await this.getSessionToken();

    return lastValueFrom(
      this.placesLib$.pipe(
        mergeMap(async (placesLib) => {
          const request: google.maps.places.AutocompleteRequest = {
            input: searchValue,

            // Bias results (same idea as before)
            locationBias: {
              center: { lat: 33.9999638, lng: -84.0658614 },
              radius: 50000,
            },

            // Replacement for `types`
            includedPrimaryTypes: [
              'street_address',
              'premise',
              'subpremise',
            ],

            sessionToken,

            // Country restriction
            region: 'US',
          };

          const { suggestions } =
            await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          return suggestions ?? [];
        }),
        catchError(() => of([])),
        take(1),
      ),
    );
  };

  private getSessionToken = async () => {
    if (!this._sessionToken) {
      clearTimeout(this._resetSessionTokenTimeoutId);

      this._sessionToken = await lastValueFrom(
        this.placesLib$.pipe(
          map(lib => new lib.AutocompleteSessionToken()),
          take(1),
        ),
      );

      this._resetSessionTokenTimeoutId = setTimeout(
        () => {
          this._sessionToken = null;
        },
        1000 * 60 * 4,
      );
    }

    return this._sessionToken;
  };

  calculateDrivingDistanceInMiles = async (lat1, lng1, lat2, lng2) => {
    const geo1 = new google.maps.LatLng(lat1, lng1);
    const geo2 = new google.maps.LatLng(lat2, lng2);
    let distance = 0;
    const distanceService = new google.maps.DistanceMatrixService();
    await distanceService.getDistanceMatrix(
      {
        origins: [geo1],
        destinations: [geo2],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.IMPERIAL, // miles is imperial metric
        avoidHighways: false,
        avoidTolls: false,
      },
      (response, status) => {
        console.log('response ', response);
        if (status !== google.maps.DistanceMatrixStatus.OK) {
          console.log('Error:', status);
          distance = 0;
        } else {
          const distanceInMeters = response?.rows[0]?.elements[0]?.distance?.value;
          distance = parseFloat((distanceInMeters * 0.000621371).toFixed(0)); // convert meters to miles
          // console.log('distanceText', response?.rows[0]?.elements[0]?.distance?.text);
        }
      },
    );
    return distance;
  };
}
